import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, isAuthenticated, checkApiHealth, initializeDatabase } from '../services/api';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';

const LoginContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(3)
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 450,
  width: '100%',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
}));

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2)
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
  padding: theme.spacing(1.2),
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark
  }
}));

const Logo = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: 'center'
}));

const Login = () => {
  const [formData, setFormData] = useState({
    documento: '',
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState(null);
  const navigate = useNavigate();

  // Verificar se o usuário já está autenticado
  useEffect(() => {
    console.log('Login component mounted');
    
    // Verificar o estado de autenticação
    const authenticated = isAuthenticated();
    console.log('isAuthenticated() =', authenticated);
    console.log('localStorage =', {
      auth_token: localStorage.getItem('auth_token'),
      user_data: localStorage.getItem('user_data')
    });
    
    if (authenticated) {
      console.log('Usuário já autenticado, redirecionando...');
      navigate('/');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Função para testar a conexão com a API
  const testApiConnection = async () => {
    setApiStatus(null);
    setLoading(true);
    
    try {
      const response = await checkApiHealth();
      setApiStatus({
        status: 'success',
        message: 'API está funcionando: ' + response.status
      });
    } catch (error) {
      console.error('Erro ao verificar saúde da API:', error);
      setApiStatus({
        status: 'error',
        message: 'API está indisponível: ' + (error.message || 'Erro desconhecido')
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Função para inicializar o banco de dados
  const handleInitializeDatabase = async () => {
    setApiStatus(null);
    setLoading(true);
    
    try {
      const response = await initializeDatabase();
      setApiStatus({
        status: 'success',
        message: 'Banco de dados inicializado com sucesso: ' + (response.message || 'Completo')
      });
    } catch (error) {
      console.error('Erro ao inicializar banco de dados:', error);
      setApiStatus({
        status: 'error',
        message: 'Falha ao inicializar banco de dados: ' + (error.message || 'Erro desconhecido')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setApiStatus(null);
    setLoading(true);

    try {
      const { documento, username, password } = formData;
      
      if (!documento || !username || !password) {
        setError('Preencha todos os campos');
        setLoading(false);
        return;
      }

      console.log('Iniciando login com documento:', documento, 'e usuário:', username);
      console.log('Fazendo requisição à API...');
      
      const loginResponse = await login(documento, username, password);
      
      console.log('Resposta da API de login:', loginResponse);
      console.log('Token recebido:', loginResponse.token ? 'Sim' : 'Não');
      console.log('LocalStorage após login:', {
        auth_token: localStorage.getItem('auth_token'),
        user_data: localStorage.getItem('user_data')
      });
      
      if (loginResponse.success) {
        console.log('Login bem-sucedido! Redirecionando...');
        navigate('/');
      } else {
        console.error('Login retornou sucesso=false:', loginResponse);
        setError('Erro na resposta da API: ' + (loginResponse.error || 'Falha desconhecida'));
      }
    } catch (error) {
      console.error('Erro capturado no componente Login:', error);
      console.error('Detalhes do erro:', {
        message: error.message,
        stack: error.stack,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data
        } : 'Sem resposta'
      });
      setError(error.message || 'Falha ao realizar login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer maxWidth="sm">
      <LoginPaper elevation={3}>
        <Logo>
          <Typography variant="h4" component="h1" color="primary" fontWeight="bold">
            RotasMais
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Sistema de Otimização de Rotas
          </Typography>
        </Logo>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        {apiStatus && (
          <Alert severity={apiStatus.status === 'success' ? 'success' : 'error'} sx={{ width: '100%', mb: 2 }}>
            {apiStatus.message}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="documento"
            label="CNPJ ou CPF da Empresa"
            name="documento"
            autoComplete="off"
            autoFocus
            value={formData.documento}
            onChange={handleInputChange}
            disabled={loading}
            helperText="Digite o CNPJ ou CPF da empresa"
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="username"
            label="Nome de Usuário"
            name="username"
            autoComplete="username"
            value={formData.username}
            onChange={handleInputChange}
            disabled={loading}
            helperText="Digite seu nome de usuário"
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Senha"
            type="password"
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleInputChange}
            disabled={loading}
          />
          <SubmitButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Entrar'}
          </SubmitButton>

          {/* Botões de teste visíveis apenas em ambiente de desenvolvimento */}
          {process.env.REACT_APP_ENV !== 'production' && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                color="info"
                onClick={testApiConnection}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Testar API
              </Button>
              
              <Button
                variant="outlined"
                color="warning"
                onClick={handleInitializeDatabase}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Inicializar DB
              </Button>
            </Box>
          )}
        </Form>
        
        {process.env.REACT_APP_ENV !== 'production' && (
          <Box mt={2} sx={{ width: '100%' }}>
            <Typography variant="caption" color="text.secondary">
              Credenciais padrão: demo / admin123
            </Typography>
          </Box>
        )}

        <Box mt={3}>
          <Typography variant="body2" color="textSecondary" align="center">
            RotasMais © {new Date().getFullYear()}
          </Typography>
        </Box>
      </LoginPaper>
    </LoginContainer>
  );
};

export default Login;